import CommonCegjelzoAuto from "../steps/common/CommonCegjelzoAuto.jsx";
import CommonCompanyType from "../steps/common/CommonCompanyType.jsx";
import CommonFillingMethod from "../steps/common/CommonFillingMethod.jsx";
import CommonKftCegjelzoInterstitial from "../steps/common/CommonKftCegjelzoInterstitial.jsx";
import CommonKftInterstitial from "../steps/common/CommonKftInterstitial.jsx";
import CommonMoreBranch from "../steps/common/CommonMoreBranch.jsx";
import CommonRouting from "../steps/common/CommonRouting.jsx";
import NewMultiCompanyInterstitial from "../steps/common/NewMultiCompanyInterstitial.jsx";
import CompanyCegjelzoTaxForm from "../steps/company/CompanyCegjelzoTaxForm.jsx";
import CompanyContactPage from "../steps/company/CompanyContactPage.jsx";
import CompanyEmployeeNo from "../steps/company/CompanyEmployeeNo.jsx";
import CompanyForeignTrade, {
  companyIndustryHasForeignTradeStep,
} from "../steps/company/CompanyForeignTrade.jsx";
import CompanyIndustryActivity, {
  companyIndustryHasActivityContent,
} from "../steps/company/CompanyIndustryActivity.jsx";
import CompanyIndustrySelect from "../steps/company/CompanyIndustrySelect.jsx";
import CompanyQuickResult from "../steps/company/CompanyQuickResult.jsx";
import CompanyRevenue from "../steps/company/CompanyRevenue.jsx";
import CompanyTaxForm from "../steps/company/CompanyTaxForm.jsx";
import CompanyVatStatus from "../steps/company/CompanyVatStatus.jsx";
import EvCegjelzoTaxForm from "../steps/ev/EvCegjelzoTaxForm.jsx";
import EvContactPage from "../steps/ev/EvContactPage.jsx";
import EvForeignTradeStep, {
  evIndustryHasForeignTradeStep,
} from "../steps/ev/EvForeignTrade.jsx";
import EvIndustryActivity, {
  evIndustryHasActivityContent,
} from "../steps/ev/EvIndustryActivity.jsx";
import EvIndustrySelect from "../steps/ev/EvIndustrySelect.jsx";
import EvQuickResult from "../steps/ev/EvQuickResult.jsx";
import EvRevenue from "../steps/ev/EvRevenue.jsx";
import EvTaxForm from "../steps/ev/EvTaxForm.jsx";
import EvVatStatus from "../steps/ev/EvVatStatus.jsx";
import NewCompanyType from "../steps/other/NewCompanyType.jsx";
import { isEvCompanyType } from "./cegjelzo/mapCegjelzoCompanyData.js";

const CONTACT_STEPS = new Set([EvContactPage, CompanyContactPage]);

/** Shared prefix for existing-business ASP (EV / company) search flows. */
const ASP_EXISTING_BUSINESS_PREFIX = [CommonRouting, CommonMoreBranch];

/**
 * NewCompanyType hands off to a completely different flow (vaf/reg/kftreg) via a
 * hard navigation, each with its own independent progress scale — there's no
 * shared step count to compute a "real" fraction from. Fixed low value, comfortably
 * under every destination's own first-step value (vaf ~33%, reg/kftreg ~25%), so
 * the bar never visibly jumps backward right after the handoff. Re-check this
 * if any of those flows' step counts change.
 */
const NEW_COMPANY_TYPE_PROGRESS_VALUE = 0.15;

/**
 * NewMultiCompanyInterstitial hands off to the multi-company Typeform via a
 * hard navigation and was never part of any countable path before (it's a
 * hard exit off CommonMoreBranch, same shape as NewCompanyType's handoff).
 * Fixed low value, same reasoning as NEW_COMPANY_TYPE_PROGRESS_VALUE above.
 */
const NEW_MULTI_COMPANY_INTERSTITIAL_PROGRESS_VALUE = 0.1;

const withAspPrefix = (steps) => [...ASP_EXISTING_BUSINESS_PREFIX, ...steps];

const AUTO_EV_PATH = withAspPrefix([
  CommonFillingMethod,
  CommonCegjelzoAuto,
  EvCegjelzoTaxForm,
  EvContactPage,
]);

/**
 * "kft" (inferred by Cégjelző auto-lookup) gets an extra consultation
 * interstitial between CommonCegjelzoAuto and CompanyCegjelzoTaxForm — every
 * other non-EV auto-inferred type skips straight to CompanyCegjelzoTaxForm.
 */
const buildAutoCompanyPath = (companytype) => {
  const path = withAspPrefix([CommonFillingMethod, CommonCegjelzoAuto]);
  if (companytype === "kft") {
    path.push(CommonKftCegjelzoInterstitial);
  }
  path.push(CompanyCegjelzoTaxForm, CompanyContactPage);
  return path;
};

/** Manual EV path with all optional steps included (max length before contact). */
const MANUAL_EV_MAX_PATH = withAspPrefix([
  CommonFillingMethod,
  CommonCompanyType,
  EvIndustrySelect,
  EvIndustryActivity,
  EvQuickResult,
  EvTaxForm,
  EvVatStatus,
  EvRevenue,
  EvForeignTradeStep,
  EvContactPage,
]);

/** Manual company path with all optional steps included (max length before contact). */
const MANUAL_COMPANY_MAX_PATH = withAspPrefix([
  CommonFillingMethod,
  CommonCompanyType,
  CommonKftInterstitial,
  CompanyIndustrySelect,
  CompanyIndustryActivity,
  CompanyQuickResult,
  CompanyTaxForm,
  CompanyEmployeeNo,
  CompanyVatStatus,
  CompanyRevenue,
  CompanyForeignTrade,
  CompanyContactPage,
]);

const isManualCompanyBranch = (selection) =>
  Boolean(
    selection &&
      selection !== "entrepreneur" &&
      selection !== "other" &&
      selection !== "reg_industry_select",
  );

const isAutoCompanyBranch = (self, activeStep, steps) => {
  const stepComponent = steps[activeStep]?.component;
  if (stepComponent === CompanyCegjelzoTaxForm) {
    return true;
  }
  if (stepComponent === EvCegjelzoTaxForm) {
    return false;
  }

  const companytype = self.props?.company_store?.companytype;
  if (companytype) {
    return !isEvCompanyType(companytype);
  }

  const cegjelzoType = self.props?.company_store?.cegjelzoData?.type;
  if (cegjelzoType) {
    return !isEvCompanyType(cegjelzoType);
  }

  return false;
};

const buildManualEvPath = (industry) => {
  const path = withAspPrefix([
    CommonFillingMethod,
    CommonCompanyType,
    EvIndustrySelect,
  ]);
  if (evIndustryHasActivityContent(industry)) {
    path.push(EvIndustryActivity);
  }
  path.push(
    EvQuickResult,
    EvTaxForm,
    EvVatStatus,
    EvRevenue,
  );
  if (evIndustryHasForeignTradeStep(industry)) {
    path.push(EvForeignTradeStep);
  }
  path.push(EvContactPage);
  return path;
};

const buildManualCompanyPath = (industry, companyTypeFlowSelection) => {
  const path = withAspPrefix([CommonFillingMethod, CommonCompanyType]);
  if (companyTypeFlowSelection === "kft") {
    path.push(CommonKftInterstitial);
  }
  path.push(CompanyIndustrySelect);
  if (companyIndustryHasActivityContent(industry)) {
    path.push(CompanyIndustryActivity);
  }
  path.push(
    CompanyQuickResult,
    CompanyTaxForm,
    CompanyEmployeeNo,
    CompanyVatStatus,
    CompanyRevenue,
  );
  if (companyIndustryHasForeignTradeStep(industry)) {
    path.push(CompanyForeignTrade);
  }
  path.push(CompanyContactPage);
  return path;
};

const resolveProgressPath = (self, activeStep, steps) => {
  const stepComponent = steps[activeStep]?.component;
  const { fillingMethodSelection, companyTypeFlowSelection } = self.state || {};
  const industry = self.props?.company_store?.industry || "";
  const companytype = self.props?.company_store?.companytype;

  if (
    stepComponent === CommonRouting ||
    stepComponent === CommonMoreBranch ||
    stepComponent === CommonFillingMethod
  ) {
    return MANUAL_COMPANY_MAX_PATH;
  }

  if (fillingMethodSelection === "auto") {
    return isAutoCompanyBranch(self, activeStep, steps)
      ? buildAutoCompanyPath(companytype)
      : AUTO_EV_PATH;
  }

  if (companyTypeFlowSelection === "entrepreneur") {
    return buildManualEvPath(industry);
  }

  if (isManualCompanyBranch(companyTypeFlowSelection)) {
    return buildManualCompanyPath(industry, companyTypeFlowSelection);
  }

  return MANUAL_COMPANY_MAX_PATH;
};

/**
 * Progress fill for a step index.
 * The path always ends with a hidden contact page (100%); visible steps never reach full fill.
 * @returns {number | null}
 */
export const computeAspProgressValue = (self, activeStep, steps) => {
  const stepComponent = steps[activeStep]?.component;
  if (!stepComponent || CONTACT_STEPS.has(stepComponent)) {
    return null;
  }

  if (stepComponent === NewCompanyType) {
    return NEW_COMPANY_TYPE_PROGRESS_VALUE;
  }

  if (stepComponent === NewMultiCompanyInterstitial) {
    return NEW_MULTI_COMPANY_INTERSTITIAL_PROGRESS_VALUE;
  }

  const path = resolveProgressPath(self, activeStep, steps);
  const pathIndex = path.indexOf(stepComponent);
  if (pathIndex === -1) {
    return null;
  }

  const totalSteps = path.length;
  if (totalSteps <= 1) {
    return 1;
  }

  return Math.min(1, Math.max(0, (pathIndex + 1) / totalSteps));
};

/**
 * ASP EV / Company flow progress (contact page excluded from display).
 * Value is stored on the flow instance and only updated on step navigation.
 * @returns {{ value: number } | null}
 */
export const getAspSearchFlowProgress = (self, activeStep, steps) => {
  const stepComponent = steps[activeStep]?.component;
  if (!stepComponent || CONTACT_STEPS.has(stepComponent)) {
    return null;
  }

  const stored = self.state?.aspProgressValue;
  const storedStep = self.state?.aspProgressStep;

  // Committed on navigation — frozen for the whole step, regardless of in-step selections.
  if (stored != null && storedStep === activeStep) {
    return { value: stored };
  }

  const path = resolveProgressPath(self, activeStep, steps);
  if (path.indexOf(stepComponent) === -1) {
    return null;
  }

  const value = computeAspProgressValue(self, activeStep, steps);
  if (value === null) {
    return null;
  }

  return { value };
};

export {
  AUTO_EV_PATH,
  buildAutoCompanyPath,
  buildManualCompanyPath,
  MANUAL_COMPANY_MAX_PATH,
  MANUAL_EV_MAX_PATH,
  resolveProgressPath,
};
